import { select, fork, call, put } from 'redux-saga/effects';
import actions from '../actions';
import queryString from 'query-string';
import { GLTFLoader, OBJLoader, STLLoader } from 'three-stdlib';
import { useInterpreterModel } from '../components/viewer3d/InterpreterModel';
import { interpretFileData } from './interpreter';
import { Color, Group, Mesh, MeshStandardMaterial } from 'three';
import { InterpreterCache } from '../gcode/interpreter-cache';
import { demoSaga } from './demo';
import ProbingInterpreter from '../probing/interpreter';
import machines from '../machines';
import { useCameraControls } from '../components/viewer3d/CameraControls';

const LINKS_URL = "https://links.pocketnc.com/";

// local dev URL - TODO - add an env variable
// to make development easier
//const LINKS_URL = "http://localhost:4500/";

const DEMOS_URL = "https://demos.pocketnc.com/";
//const DEMOS_URL = "http://localhost:4600/http://localhost:4500/";
export const DEFAULT_GCODE = `%
G20
G90 G94 G40 G17
G53 G0 Z0
G0 A0 B0
G0 X0
M5
M0
T4 M6
G43
S8500 M3
F20

G0 A90
G0 X1 Y1
G1 Z0
G1 X-1
G1 y-1
G1 X1
G1 Y1

G49
G53 G0 Z0

M5
M30
%
`;

export const fetchContent = async (url) => {
  const content = await fetch(url).then((req) => req.arrayBuffer());

  return content;
};

export const fetchPostUrls = async (post) => {
  const urls = await fetch(LINKS_URL + "?post=" + encodeURI(post)).then((res) => res.json());

  return urls;
};

export default function* loadQueryParams() {
  const query = queryString.parse(window.location.search);

  if(query.demo === "1") {
    yield fork(demoSaga, query.qr);
  }

  if(query.titleBar === "hide") {
    yield put(actions.ui.titleBar.hide());
  }

  if(query.gcodePane === "hide") {
    yield put(actions.ui.gcodePane.hide());
  }

  if(query.cameraDistance) {
    const dist = parseFloat(query.cameraDistance); 
    if(!isNaN(dist)) {
      const cameraState = useCameraControls.getState();
      cameraState.setRadius(dist);
    }
  }
  if(query.theta && query.phi) {
    const theta = parseFloat(query.theta)*Math.PI/180; 
    const phi = parseFloat(query.phi)*Math.PI/180; 
    if(!isNaN(theta) && !isNaN(phi)) {
      const cameraState = useCameraControls.getState();
      cameraState.setCameraOrientation(theta, phi);
    }
  }

  if(query.showBetweenLines && (query.showBetweenLines === "true" || query.showBetweenLines === "false")) {
    const showBetweenLines = query.showBetweenLines === "true";
    yield put(actions.viewer3d.setShowBetweenLines(showBetweenLines));
  }

  if(query.minLine) {
    const minLine = parseInt(query.minLine);
    if(!isNaN(minLine)) {
      yield put(actions.viewer3d.setMinLine(minLine));
    }
  }

  if(query.maxLine) {
    const maxLine = parseInt(query.maxLine);
    if(!isNaN(maxLine)) {
      yield put(actions.viewer3d.setMaxLine(maxLine));
    }
  }

  if(query.automaticallyUpdateLines && (query.automaticallyUpdateLines === "true" || query.automaticallyUpdateLines === "false")) {
    const automaticallyUpdateLines = query.automaticallyUpdateLines === "true";
    yield put(actions.viewer3d.setAutomaticallyUpdateLines(automaticallyUpdateLines));
  }

  if(query.tool) {
    try {
      let tools = [];
      if(Array.isArray(query.tool)) {
        tools = query.tool.map((json) => JSON.parse(json));
      } else {
        tools.push(JSON.parse(query.tool));
      }
      for(let tool of tools) {
        yield put(actions.machineState.tool.table.setTool(tool));
      }
    } catch(err) {
      console.error("Error parsing tools specified in query string.", err);
    }
  }

  if(query.wcs) {
    try {
      let wcsObjs = [];
      if(Array.isArray(query.wcs)) {
        wcsObjs = query.wcs.map((json) => JSON.parse(json));
      } else {
        wcsObjs.push(JSON.parse(query.wcs));
      }
      for(let wcsObj of wcsObjs) {
        const { wcs, X, Y, Z, A, B, C } = wcsObj;

        const xParsed = parseFloat(X);
        const yParsed = parseFloat(Y);
        const zParsed = parseFloat(Z);
        const aParsed = parseFloat(A);
        const bParsed = parseFloat(B);
        const cParsed = parseFloat(C);

        const xValue = isNaN(xParsed) ? 0 : xParsed;
        const yValue = isNaN(yParsed) ? 0 : yParsed;
        const zValue = isNaN(zParsed) ? 0 : zParsed;
        const aValue = isNaN(aParsed) ? 0 : aParsed;
        const bValue = isNaN(bParsed) ? 0 : bParsed;
        const cValue = isNaN(cParsed) ? 0 : cParsed;

        yield put(actions.machineState.motion.g5x.setG5xWorkOffset({ index: wcs, axis: "X", value: xValue }));
        yield put(actions.machineState.motion.g5x.setG5xWorkOffset({ index: wcs, axis: "Y", value: yValue }));
        yield put(actions.machineState.motion.g5x.setG5xWorkOffset({ index: wcs, axis: "Z", value: zValue }));
        yield put(actions.machineState.motion.g5x.setG5xWorkOffset({ index: wcs, axis: "A", value: aValue }));
        yield put(actions.machineState.motion.g5x.setG5xWorkOffset({ index: wcs, axis: "B", value: bValue }));
        yield put(actions.machineState.motion.g5x.setG5xWorkOffset({ index: wcs, axis: "C", value: cValue }));
      }
    } catch(err) {
      console.error("Error parsing work coordinate systems specified in query string.", err);
    }
  }

  if(query.machine) {
    yield put(actions.machineState.machine.set(query.machine));
  }

  if(query.feedColor) {
    const color = new Color(query.feedColor);
    yield put(actions.viewer3d.setFeedColor(color.getHex()));
  }

  if(query.rapidColor) {
    const color = new Color(query.rapidColor);
    yield put(actions.viewer3d.setRapidColor(color.getHex()));
  }

  if(query.plotOffset) {
    const offset = parseFloat(query.plotOffset);
    if(!isNaN(offset)) {
      yield put(actions.viewer3d.setOffset(offset));
    }
  }

  let postUrls = {};
  if(query.post) {
    postUrls = yield call(fetchPostUrls, query.post);
  }

  if(query.model) {
    const model = yield call(fetchContent, DEMOS_URL + query.model);
    if(query.model.toLowerCase().endsWith(".obj")) {
      const decoder = new TextDecoder("utf-8");
      const objLoader = new OBJLoader();

      const obj = objLoader.parse(decoder.decode(model));
      useInterpreterModel.getState().setModel(obj);
    } else if(query.model.toLowerCase().endsWith(".stl")) {
      const stlLoader = new STLLoader();

      const geometry = stlLoader.parse(model);
      const mesh = new Mesh(geometry, new MeshStandardMaterial());
      const obj = new Group();
      obj.add(mesh);
      useInterpreterModel.getState().setModel(obj);
    } else if(query.model.toLowerCase().endsWith(".glb")) {
      yield new Promise((resolve) => {
        const gltfLoader = new GLTFLoader();

        gltfLoader.parse(model, "", (obj) => {
          const nestedObj = new Group();
          nestedObj.add(obj.scene);
          useInterpreterModel.getState().setModel(nestedObj);
          resolve();
        });
      });
    }
    yield put(actions.viewer3d.loadModel());
    yield put(actions.viewer3d.showModel());
  } else if(postUrls.model) {
    const model = yield call(fetchContent, postUrls.model);
    if(postUrls.model.toLowerCase().endsWith(".obj")) {
      const decoder = new TextDecoder("utf-8");
      const objLoader = new OBJLoader();

      const obj = objLoader.parse(decoder.decode(model));
      useInterpreterModel.getState().setModel(obj);
    } else if(postUrls.model.toLowerCase().endsWith(".stl")) {
      const decoder = new TextDecoder("utf-8");
      const stlLoader = new STLLoader();

      const obj = stlLoader.parse(decoder.decode(model));
      useInterpreterModel.getState().setModel(obj);
    } else if(postUrls.model.toLowerCase().endsWith(".glb")) {
      yield new Promise((resolve) => {
        const gltfLoader = new GLTFLoader();

        gltfLoader.parse(model, "", (obj) => {
          useInterpreterModel.getState().setModel(obj.scene);
          resolve();
        });
      });
    }
    yield put(actions.viewer3d.loadModel());
    yield put(actions.viewer3d.showModel());
  }

  const hash = window.location.hash;
  if(hash.indexOf("probing") !== -1) {
    const machine = machines[yield select( (state) => state.machineState.machine )];
    yield put(actions.machineState.joints.set(machine.safeJoints));

    yield put(actions.machineState.tool.set(14));
    yield put(actions.machineState.tool.change());
    yield put(actions.machineState.tool.setToolLengthOffset({ H: 14 }));

    const probingProgram = yield select( (state) => state.probing.program );
    const probingInterpreter = new ProbingInterpreter(probingProgram, machine);
    const gcodeLines = [];
    probingInterpreter.gcodeLines.forEach((chunk) => {
      gcodeLines.push(...chunk);
    });
    yield call(interpretFileData, gcodeLines);
  } else {
    if(query.program) {
      yield put(actions.interpreter.setFileName(query.program));
      yield call(interpretFileData, { url: DEMOS_URL + query.program });
    } else if(postUrls.program) {
      yield put(actions.interpreter.setFileName(postUrls.programName || `post.ngc`));
      yield call(interpretFileData, { url: postUrls.program });
    } else {
      const gcode = DEFAULT_GCODE;
      yield call(interpretFileData, gcode);
    }
  }
  yield new Promise((resolve) => {
    InterpreterCache.getInstance().addEventListener("end", resolve)
  });

  // Actions placed here will happen after the interpreter completes
  // interpretting the entire program

  if(query.currentLine) {
    const line = parseInt(query.currentLine);
    if(!isNaN(line)) {
      yield put(actions.interpreter.setCurrentLine(line));
    }
  }

  if(query.t) {
    const t = parseFloat(query.t);
    if(!isNaN(t)) {
      yield put(actions.interpreter.setCurrentLineWithTime(t));
    }
  }
}
